import { useCallback, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Youtube, Instagram, Linkedin, X, Menu } from "lucide-react";

const AppHeader = () => {
  const navigate = useNavigate();
  const goToSignup = useCallback(() => navigate("/signup"), [navigate]);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  return (
    <header className="fixed top-0 w-full z-50 backdrop-blur-md border-b border-white/10 bg-black/70">
      <div className="max-w-7xl mx-auto flex items-center justify-between px-6 md:px-12 py-4">
        <div className="flex items-center gap-10">
          <Link
            to="/"
            className="text-2xl font-bold tracking-wide bg-gradient-to-r from-purple-300 via-violet-400 to-purple-500 bg-clip-text text-transparent cursor-pointer"
          >
            XynexAI
          </Link>

          <nav className="hidden md:flex items-center gap-6 text-lg font-medium text-gray-300">
            <Link to="/use-case" className="hover:text-purple-400 transition">
              Use Cases
            </Link>
            <Link to="/pricing" className="hover:text-purple-400 transition">
              Pricing
            </Link>
            <Link to="/about" className="hover:text-purple-400 transition">
              About Us
            </Link>
            <Link to="/security" className="hover:text-purple-400 transition">
              Security
            </Link>
          </nav>
        </div>

        <div className="flex items-center gap-4">
          <button
            onClick={goToSignup}
            className="hidden md:block px-6 py-2 rounded-full font-semibold text-white bg-gradient-to-r from-purple-500 to-pink-500 hover:scale-105 transition"
          >
            Try for Free
          </button>
          <button
            className="md:hidden text-gray-300 hover:text-white"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? <X /> : <Menu />}
          </button>
        </div>
      </div>

      {isMobileMenuOpen && (
        <div className="md:hidden bg-black/95 border-b border-white/10 py-4 px-6 space-y-4 absolute w-full top-full left-0">
          <nav className="flex flex-col gap-4 text-lg font-medium text-gray-300">
            <Link
              to="/use-case"
              className="hover:text-purple-400 transition"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              Use Cases
            </Link>
            <Link
              to="/pricing"
              className="hover:text-purple-400 transition"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              Pricing
            </Link>
            <Link
              to="/about"
              className="hover:text-purple-400 transition"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              About Us
            </Link>
            <Link
              to="/security"
              className="hover:text-purple-400 transition"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              Security
            </Link>
            <button
              onClick={() => {
                goToSignup();
                setIsMobileMenuOpen(false);
              }}
              className="w-full px-6 py-2 rounded-full font-semibold text-white bg-gradient-to-r from-purple-500 to-pink-500 hover:scale-105 transition text-center"
            >
              Try for Free
            </button>
          </nav>
        </div>
      )}
    </header>
  );
};

const AppFooter = () => {
  return (
    <footer className="bg-black text-white mt-32">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-12">
          <div className="md:col-span-2">
            <div className="text-3xl font-bold tracking-wide mb-6">
              <Link
                to="/"
                className="font-bold tracking-wide bg-gradient-to-r from-purple-300 via-violet-400 to-purple-500 bg-clip-text text-transparent cursor-pointer"
              >
                XynexAI
              </Link>
            </div>

            <div className="flex items-center gap-4 text-gray-300">
              <Link to="https://www.youtube.com/@InteliQuantAI" target="_blank" rel="noopener noreferrer" className="hover:text-white cursor-pointer">
               <Youtube />
              </Link>
              <span className="hover:text-white cursor-pointer">
                <Instagram />
              </span>
              <Link to="https://x.com/inteliquant_ai" target="_blank" rel="noopener noreferrer" className="hover:text-white cursor-pointer">
                <X />
              </Link>
              <Link to="https://in.linkedin.com/company/inteliquantai" target="_blank" rel="noopener noreferrer" className="hover:text-white cursor-pointer">
               <Linkedin />
              </Link>
            </div>
          </div>

          <div>
            <h4 className="font-semibold mb-4">Get Started</h4>
            <ul className="space-y-2 text-gray-300">
              <li>
                <Link to="/pricing" className="hover:text-white">Pricing</Link>
              </li>
              <li>
                <Link to="/signup" className="hover:text-white">Demo</Link></li>
            </ul>
          </div>

          <div>
            <h4 className="font-semibold mb-4">Company</h4>
            <ul className="space-y-2 text-gray-300">
              <li><Link to="/about" className="hover:text-white">About Us</Link></li>
              <li><Link to="/contact-us" className="hover:text-white">Contact Us</Link></li>
            </ul>
          </div>

          <div>
            <h4 className="font-semibold mb-4">Legal</h4>
            <ul className="space-y-2 text-gray-300">
              <li><Link to="/terms-of-use" className="hover:text-white">Terms of Use</Link></li>
              <li><Link to="/privacy-policy" className="hover:text-white">Privacy Policy</Link></li>
              <li><Link to="/security" className="hover:text-white">Data Security</Link></li>
            </ul>
          </div>
        </div>

        <div className="border-t border-white/10 mt-12 pt-6 text-center text-sm text-gray-300">
           © {new Date().getFullYear()} InteliquantAI Pvt Ltd. All rights reserved.
        </div>
      </div>
    </footer>
  );
};

const MainLayout = ({ children }) => {
  return (
    <div className="min-h-screen bg-black text-white flex flex-col">
      <AppHeader />
      {children}
      <AppFooter />
    </div>
  );
};

export default MainLayout;